import React, { useState, useEffect } from 'react';
import CustomToggleSwitch from '../../../custom-inputs/CustomToggleSwitch';
import CustomSelect from '../../../custom-inputs/CustomSelect';
import CustomTextInput from '../../../custom-inputs/CustomTextInput';
import ItemSelectInput from '../../../custom-inputs/ItemSelectInput';
import RoundState from '../../../_types/RoundState';
import { RulesAndRewardsViewContainer } from './Round.styles';
import NewGameState from '../../../_types/NewGameState';
import { Box, Typography } from '@mui/material';
import { SectionTitle } from '../../../GameFormComponents';
import { GamifyToast } from 'application/common/CustomToasts';
import RoundErrorMessagesState from '../../../_types/RoundErrorMessagesState';
import featureFlag from 'application/common/featureFlag';

const hideSetterCloser = featureFlag('noSetterCloser');

interface Props {
  onChangeRoundState: (roundState: RoundState) => void;
  roundState: RoundState;
  kpis: { label: string; value: string; rule: any }[];
  newGameState: NewGameState;
  onChangeNewGameState: (roundState: NewGameState) => void;
  tabNo: number;
  errorMessages?: RoundErrorMessagesState;
}

interface KPI {
  label: string;
  value: string;
  rule: any;
}

// interface MissionRule {
//   id: string;
//   name: string;
//   multiplier: number;
//   role: string;
//   sequence: number;
// }

function NumberInput(props: any) {
  const [text, setText] = useState(props.value);
  return (
    <CustomTextInput
      label={props.label}
      onChange={(newValue) => {
        // Regex to check if newValue is a number or not (includes decimals, using parseFloat prevents reliable usage on isNaN for checking this)
        const isNumber = /^-?\d*\.?\d*$/.test(newValue);
        if (newValue === '' || isNumber) {
          setText(newValue);
          props.onChangeText(newValue);
        }
      }}
      value={text}
    />
  );
}

const renderMultipleKpiInputs = (
  newGameState: NewGameState,
  onChangeNewGameState: (newGameState: NewGameState) => void,
  roundState: RoundState,
  onChangeRoundState: (roundState: RoundState) => void,
  kpis: { label: string; value: string; rule: any }[],
  normalizedKPIs: any,
  tabNo: number,
  newPointsPerAction: string,
  setNewPointsPerAction: React.Dispatch<React.SetStateAction<string>>,
  errorMessages?: RoundErrorMessagesState
): React.JSX.Element => {
  // Use missionRules as the primary data structure
  const missionRules = roundState.missionRules || [];

  console.log(
    'renderMultipleKpiInputs for round',
    tabNo,
    '- missionRules:',
    JSON.stringify(missionRules)
  );

  const addKpiRule = () => {
    console.log('Add KPI button clicked for round', tabNo);
    console.log('Current round state:', {
      KPIRuleID: roundState.KPIRuleID,
      pointsPerAction: newPointsPerAction,
      currentRules: missionRules.length,
    });

    // If we don't have any KPIs selected in state, try to get one from the dropdown
    const selectedKpiId =
      roundState.KPIRuleID || (kpis.length > 0 ? kpis[0].value : '');

    if (!selectedKpiId) {
      alert('No KPIs available to add');
      return;
    }

    // If missionRules is empty, we may need to create the first entry
    if (missionRules.length === 0) {
      const pointsValue = parseFloat(newPointsPerAction) || 1.0;

      // Create first rule
      const newRule = {
        id: selectedKpiId,
        multiplier: pointsValue,
        name: normalizedKPIs[selectedKpiId]?.label || '',
        role: 'contestant',
        sequence: 1,
      };

      console.log(
        'Adding first KPI rule for round',
        tabNo,
        ':',
        JSON.stringify(newRule)
      );

      // Update roundState
      const updatedRoundState = {
        ...roundState,
        missionRules: [newRule],
        rules: [newRule],
        KPIRuleID: newRule.id,
        KPIRuleName: newRule.name,
      };

      onChangeRoundState(updatedRoundState);

      // Update newGameState as well
      const newGameStateCopy = { ...newGameState };
      if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
        newGameStateCopy.bracket.roundValues[tabNo - 1] = {
          ...newGameStateCopy.bracket.roundValues[tabNo - 1],
          ...updatedRoundState,
        };
      } else {
        newGameStateCopy.bracket.roundValues.push(updatedRoundState);
      }

      // If this is the last round, also update the main KPIRuleID
      if (tabNo === newGameState.totalRounds) {
        newGameStateCopy.KPIRuleID = newRule.id;
        newGameStateCopy.KPIRuleName = newRule.name;
      }

      onChangeNewGameState(newGameStateCopy);

      // Reset inputs
      setNewPointsPerAction('1.0');

      // Find next available KPI and select it
      const usedKpiIds = [newRule.id];
      const nextAvailableKpi = kpis.find(
        (kpi) => !usedKpiIds.includes(kpi.value)
      );

      if (nextAvailableKpi) {
        // Update roundState with the next KPI
        onChangeRoundState({
          ...updatedRoundState,
          KPIRuleID: nextAvailableKpi.value,
          KPIRuleName: nextAvailableKpi.label,
        });

        // Update game state
        if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
          newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleID =
            nextAvailableKpi.value;
          newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleName =
            nextAvailableKpi.label;
          onChangeNewGameState(newGameStateCopy);
        }
      }

      return;
    }

    // Make sure we don't add duplicate KPI rules
    const existingRule = missionRules.find((rule) => rule.id === selectedKpiId);
    if (existingRule) {
      // Find an available KPI that hasn't been used yet
      const usedKpiIds = missionRules.map((rule) => rule.id);
      const availableKpis = kpis.filter(
        (kpi) => !usedKpiIds.includes(kpi.value)
      );

      if (availableKpis.length === 0) {
        alert('All KPIs have already been added');
        return;
      }

      // Automatically select the first available KPI
      const suggestedKpi = availableKpis[0];
      console.log(
        'Selecting available KPI for round',
        tabNo,
        ':',
        suggestedKpi.label
      );

      // Update roundState with the suggested KPI
      onChangeRoundState({
        ...roundState,
        KPIRuleID: suggestedKpi.value,
        KPIRuleName: suggestedKpi.label,
      });

      // Update newGameState as well
      const newGameStateCopy = { ...newGameState };
      if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
        newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleID =
          suggestedKpi.value;
        newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleName =
          suggestedKpi.label;
      } else {
        newGameStateCopy.bracket.roundValues.push({
          ...roundState,
          KPIRuleID: suggestedKpi.value,
          KPIRuleName: suggestedKpi.label,
        });
      }

      // If this is the last round, also update the main KPIRuleID
      if (tabNo === newGameState.totalRounds) {
        // Use the first rule from the existing rules for the main game KPI
        if (missionRules.length > 0) {
          const firstRule = missionRules[0];
          newGameStateCopy.KPIRuleID = firstRule.id;
          newGameStateCopy.KPIRuleName = firstRule.name;
        }
      }

      onChangeNewGameState(newGameStateCopy);

      return;
    }

    const pointsValue = parseFloat(newPointsPerAction) || 1.0;

    const newRule = {
      id: selectedKpiId,
      multiplier: pointsValue,
      name: normalizedKPIs[selectedKpiId]?.label || '',
      role: 'contestant',
      sequence: missionRules.length + 1,
    };

    const updatedRules = [...missionRules, newRule];

    console.log('Adding KPI rule for round', tabNo, ':', {
      selectedKpiId,
      pointsValue,
      currentRules: missionRules.length,
      newRules: updatedRules.length,
    });

    // Update roundState with the new rules
    const updatedRoundState = {
      ...roundState,
      missionRules: updatedRules,
      rules: updatedRules,
    };

    // Reference the first rule for main KPIRuleID if this is the last round
    const firstRule = updatedRules[0];

    // Select another available KPI for the next addition
    const usedKpiIds = updatedRules.map((rule) => rule.id);
    const nextAvailableKpi = kpis.find(
      (kpi) => !usedKpiIds.includes(kpi.value)
    );

    if (nextAvailableKpi) {
      updatedRoundState.KPIRuleID = nextAvailableKpi.value;
      updatedRoundState.KPIRuleName = nextAvailableKpi.label;
    } else {
      // If no more available KPIs, use the first rule's KPI
      updatedRoundState.KPIRuleID = firstRule.id;
      updatedRoundState.KPIRuleName = firstRule.name;
    }

    onChangeRoundState(updatedRoundState);

    // Update newGameState as well
    const newGameStateCopy = { ...newGameState };
    if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
      newGameStateCopy.bracket.roundValues[tabNo - 1] = {
        ...newGameStateCopy.bracket.roundValues[tabNo - 1],
        ...updatedRoundState,
      };
    } else {
      newGameStateCopy.bracket.roundValues.push(updatedRoundState);
    }

    // If this is the last round, also update the main KPIRuleID using the first rule
    if (tabNo === newGameState.totalRounds) {
      newGameStateCopy.KPIRuleID = firstRule.id;
      newGameStateCopy.KPIRuleName = firstRule.name;
    }

    onChangeNewGameState(newGameStateCopy);

    // Reset the points per action input value
    setNewPointsPerAction('1.0');
  };

  const removeKpiRule = (index: number) => {
    console.log('Removing KPI rule at index:', index, 'for round', tabNo);
    console.log('Current rules before removal:', JSON.stringify(missionRules));

    const _missionRules = [...missionRules];
    _missionRules.splice(index, 1);

    console.log('Rules after removal:', JSON.stringify(_missionRules));

    // Update roundState with the updated rule set
    const updatedRoundState = {
      ...roundState,
      missionRules: _missionRules,
      rules: _missionRules,
    };

    // Find an appropriate KPI to select after removal
    if (_missionRules.length > 0) {
      // Find all KPIs that are currently in use
      const usedKpiIds = _missionRules.map((rule) => rule.id);

      // Try to find a KPI that isn't in use yet
      const unusedKpi = kpis.find((kpi) => !usedKpiIds.includes(kpi.value));

      if (unusedKpi) {
        // If there's an unused KPI, select it
        updatedRoundState.KPIRuleID = unusedKpi.value;
        updatedRoundState.KPIRuleName = unusedKpi.label;
        console.log('Selected unused KPI after removal:', unusedKpi.label);
      } else {
        // If all KPIs are used, select the first rule's KPI
        updatedRoundState.KPIRuleID = _missionRules[0].id;
        updatedRoundState.KPIRuleName = _missionRules[0].name;
        console.log(
          'Selected first rule KPI after removal:',
          _missionRules[0].name
        );
      }
    } else {
      // If no rules remain, select any available KPI or clear the selection
      if (kpis.length > 0) {
        updatedRoundState.KPIRuleID = kpis[0].value;
        updatedRoundState.KPIRuleName = kpis[0].label;
        console.log(
          'No rules remain, selected first available KPI:',
          kpis[0].label
        );
      } else {
        updatedRoundState.KPIRuleID = '';
        updatedRoundState.KPIRuleName = '';
        console.log('No rules remain and no KPIs available, cleared selection');
      }
    }

    onChangeRoundState(updatedRoundState);

    // Update newGameState as well
    const newGameStateCopy = { ...newGameState };
    if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
      newGameStateCopy.bracket.roundValues[tabNo - 1] = {
        ...newGameStateCopy.bracket.roundValues[tabNo - 1],
        ...updatedRoundState,
      };
    } else {
      newGameStateCopy.bracket.roundValues.push(updatedRoundState);
    }

    // If this is the last round, ensure the main KPIRuleID is also updated
    if (tabNo === newGameState.totalRounds) {
      if (_missionRules.length > 0) {
        // Use the first rule for the main game KPI
        const firstRule = _missionRules[0];
        newGameStateCopy.KPIRuleID = firstRule.id;
        newGameStateCopy.KPIRuleName = firstRule.name;
      } else if (kpis.length > 0) {
        // If no rules left but there are KPIs, use the first available
        newGameStateCopy.KPIRuleID = kpis[0].value;
        newGameStateCopy.KPIRuleName = kpis[0].label;
      } else {
        // Only clear if absolutely necessary
        newGameStateCopy.KPIRuleID = '';
        newGameStateCopy.KPIRuleName = '';
      }
    }

    onChangeNewGameState(newGameStateCopy);
  };

  const renderKPIList = missionRules.map((_missionRule, ruleNum) => {
    console.log(
      `Rendering KPI #${ruleNum} for round ${tabNo}:`,
      JSON.stringify(_missionRule)
    );

    // Determine if this is the last rule in the list
    const isLastRule = ruleNum === missionRules.length - 1;

    return (
      <Box key={`round-${tabNo}-kpi-rule-${_missionRule.id}-${ruleNum}`}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <CustomSelect
              label="KPI"
              value={_missionRule.id}
              onChange={(newValue: any) => {
                // Check if this KPI is already in use by another rule
                const duplicateIndex = missionRules.findIndex(
                  (rule, idx) => rule.id === newValue && idx !== ruleNum
                );

                if (duplicateIndex >= 0) {
                  alert('This KPI is already used in another rule');
                  return;
                }

                // Create a copy of the existing rules
                const _missionRules = [...missionRules];

                // Update just this rule's KPI
                _missionRules[ruleNum] = {
                  ..._missionRules[ruleNum],
                  id: newValue,
                  name: normalizedKPIs[newValue]?.label || '',
                };

                console.log(
                  'Updating KPI for rule',
                  ruleNum,
                  'to:',
                  newValue,
                  'in round',
                  tabNo
                );

                // Create updated roundState
                const updatedRoundState = {
                  ...roundState,
                  missionRules: _missionRules,
                  rules: _missionRules,
                };

                // Always keep KPIRuleID in sync with the first rule
                if (roundState.useMultipleKPIs && _missionRules.length > 0) {
                  updatedRoundState.KPIRuleID = _missionRules[0].id;
                  updatedRoundState.KPIRuleName = _missionRules[0].name;
                }

                // Update roundState with the modified rules
                onChangeRoundState(updatedRoundState);

                // Update newGameState as well
                const newGameStateCopy = { ...newGameState };
                if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                  newGameStateCopy.bracket.roundValues[tabNo - 1] = {
                    ...newGameStateCopy.bracket.roundValues[tabNo - 1],
                    ...updatedRoundState,
                  };
                } else {
                  newGameStateCopy.bracket.roundValues.push(updatedRoundState);
                }
                onChangeNewGameState(newGameStateCopy);
              }}
              items={kpis}
            />
          </div>
          <div style={{ flex: 1 }}>
            <NumberInput
              label={`Points Per Action*`}
              value={_missionRule.multiplier?.toString()}
              onChangeText={(newValue: string) => {
                // Create a copy of the existing rules
                const _missionRules = [...missionRules];

                // Update just this rule's multiplier
                _missionRules[ruleNum] = {
                  ..._missionRules[ruleNum],
                  multiplier: parseFloat(newValue) || 0,
                };

                console.log(
                  'Updating multiplier for rule',
                  ruleNum,
                  'to:',
                  newValue,
                  'in round',
                  tabNo
                );

                // Create updated roundState
                const updatedRoundState = {
                  ...roundState,
                  missionRules: _missionRules,
                  rules: _missionRules,
                };

                // Update roundState
                onChangeRoundState(updatedRoundState);

                // Update newGameState as well
                const newGameStateCopy = { ...newGameState };
                if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                  newGameStateCopy.bracket.roundValues[tabNo - 1] = {
                    ...newGameStateCopy.bracket.roundValues[tabNo - 1],
                    ...updatedRoundState,
                  };
                } else {
                  newGameStateCopy.bracket.roundValues.push(updatedRoundState);
                }
                onChangeNewGameState(newGameStateCopy);
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '4px' }}>
          <CustomSelect
            label="User's Role"
            value={_missionRule.role}
            onChange={(newValue) => {
              // Create a copy of the existing rules
              const _missionRules = [...missionRules];

              // Update just this rule's role
              _missionRules[ruleNum] = {
                ..._missionRules[ruleNum],
                role: newValue,
              };

              console.log(
                'Updating role for rule',
                ruleNum,
                'to:',
                newValue,
                'in round',
                tabNo
              );

              // Create updated roundState
              const updatedRoundState = {
                ...roundState,
                missionRules: _missionRules,
                rules: _missionRules,
              };

              // Update roundState
              onChangeRoundState(updatedRoundState);

              // Update newGameState as well
              const newGameStateCopy = { ...newGameState };
              if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                newGameStateCopy.bracket.roundValues[tabNo - 1] = {
                  ...newGameStateCopy.bracket.roundValues[tabNo - 1],
                  ...updatedRoundState,
                };
              } else {
                newGameStateCopy.bracket.roundValues.push(updatedRoundState);
              }
              onChangeNewGameState(newGameStateCopy);
            }}
            items={[
              {
                label: 'Anyone',
                value: 'contestant',
              },
              ...(hideSetterCloser
                ? []
                : [
                    {
                      label: 'Setter',
                      value: 'setter',
                    },
                    {
                      label: 'Closer',
                      value: 'closer',
                    },
                  ]),
            ]}
          />
        </div>

        <Box
          onClick={() => removeKpiRule(ruleNum)}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '8px',
          }}
        >
          <Typography
            style={{ color: 'red', fontSize: 12 }}
            sx={{ cursor: 'pointer' }}
          >
            Remove
          </Typography>
        </Box>

        {/* Only show the Add KPI button on the last rule */}
        {isLastRule && (
          <div style={{ marginTop: '12px', marginBottom: '8px' }}>
            <div
              onClick={addKpiRule}
              style={{
                color: '#FF6A00',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              + Add KPI
            </div>
          </div>
        )}

        <Box
          style={{
            borderBottomWidth: 0.5,
            borderBottomColor: '#D9D9D9',
            marginBottom: 16,
            paddingBottom: 16,
          }}
        />
      </Box>
    );
  });

  // If there are no mission rules but we're in multiple KPI mode, create a default KPI
  if (missionRules.length === 0 && roundState.useMultipleKPIs) {
    // Add a default KPI using the first available KPI
    if (kpis && kpis.length > 0) {
      console.log(
        'Adding default KPI to empty missionRules list for round',
        tabNo
      );

      // Create a default rule with the first KPI
      const defaultRule = {
        id: kpis[0].value,
        name: kpis[0].label,
        multiplier: 1.0,
        role: 'contestant',
        sequence: 1,
      };

      // Update the state with the default rule
      setTimeout(() => {
        // Create updated roundState
        const updatedRoundState = {
          ...roundState,
          missionRules: [defaultRule],
          rules: [defaultRule],
          KPIRuleID: defaultRule.id,
          KPIRuleName: defaultRule.name,
        };

        onChangeRoundState(updatedRoundState);

        // Update newGameState as well
        const newGameStateCopy = { ...newGameState };
        if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
          newGameStateCopy.bracket.roundValues[tabNo - 1] = {
            ...newGameStateCopy.bracket.roundValues[tabNo - 1],
            ...updatedRoundState,
          };
        } else {
          newGameStateCopy.bracket.roundValues.push(updatedRoundState);
        }

        // If this is the last round, also update the main KPIRuleID
        if (tabNo === newGameState.totalRounds) {
          newGameStateCopy.KPIRuleID = defaultRule.id;
          newGameStateCopy.KPIRuleName = defaultRule.name;
        }

        onChangeNewGameState(newGameStateCopy);

        // Find next available KPI and select it
        const usedKpiIds = [defaultRule.id];
        const nextAvailableKpi = kpis.find(
          (kpi) => !usedKpiIds.includes(kpi.value)
        );

        if (nextAvailableKpi) {
          const updatedStateWithNextKpi = {
            ...updatedRoundState,
            KPIRuleID: nextAvailableKpi.value,
            KPIRuleName: nextAvailableKpi.label,
          };

          onChangeRoundState(updatedStateWithNextKpi);

          if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
            newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleID =
              nextAvailableKpi.value;
            newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleName =
              nextAvailableKpi.label;
            onChangeNewGameState(newGameStateCopy);
          }
        }
      }, 0);

      // Return a loading placeholder
      return (
        <Box key="round-default-kpi-loading">
          <div style={{ padding: '16px', textAlign: 'center' }}>
            Loading KPI editor...
          </div>
        </Box>
      );
    }
  }

  return (
    <>
      {renderKPIList}

      {missionRules.length === 0 && (
        <>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <CustomSelect
                label="KPI*"
                value={roundState.KPIRuleID}
                error={errorMessages ? Boolean(errorMessages.KPIRuleID) : false}
                errorMessage={
                  errorMessages ? errorMessages.KPIRuleID || '' : ''
                }
                onChange={(newValue: any) => {
                  // Update the round state first
                  onChangeRoundState({
                    ...roundState,
                    KPIRuleID: newValue,
                    KPIRuleName: normalizedKPIs[newValue]?.label || '',
                  });

                  // Update newGameState for the bracket
                  const newGameStateCopy = { ...newGameState };
                  if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                    newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleID =
                      newValue;
                    newGameStateCopy.bracket.roundValues[
                      tabNo - 1
                    ].KPIRuleName = normalizedKPIs[newValue]?.label || '';
                  } else {
                    newGameStateCopy.bracket.roundValues.push({
                      ...roundState,
                      KPIRuleID: newValue,
                      KPIRuleName: normalizedKPIs[newValue]?.label || '',
                    });
                  }

                  // If this is the last round, also set the main game KPIRuleID
                  if (tabNo === newGameState.totalRounds) {
                    newGameStateCopy.KPIRuleID = newValue;
                    newGameStateCopy.KPIRuleName =
                      normalizedKPIs[newValue]?.label || '';

                    // Also log that we're doing this as a fix for validation
                  }

                  onChangeNewGameState(newGameStateCopy);
                }}
                items={kpis}
              />
            </div>
            <div style={{ flex: 1 }}>
              <NumberInput
                label="Points Per Action*"
                value={(() => {
                  if (roundState.missionRules && roundState.KPIRuleID) {
                    const rule = roundState.missionRules.find(
                      (rule) => rule.id === roundState.KPIRuleID
                    );
                    return rule?.multiplier?.toString() || '1.0';
                  }
                  return '1.0';
                })()}
                onChangeText={(newValue: string) => {
                  // Create or update the mission rule with the new multiplier value
                  let _missionRules = roundState.missionRules || [];
                  const ruleIndex = _missionRules.findIndex(
                    (rule) => rule.id === roundState.KPIRuleID
                  );

                  if (ruleIndex >= 0) {
                    // Update existing rule
                    _missionRules[ruleIndex] = {
                      ..._missionRules[ruleIndex],
                      multiplier: parseFloat(newValue),
                    };
                  } else if (roundState.KPIRuleID) {
                    // Create new rule
                    _missionRules = [
                      {
                        id: roundState.KPIRuleID,
                        name: normalizedKPIs[roundState.KPIRuleID]?.label || '',
                        multiplier: parseFloat(newValue),
                        role: 'contestant',
                        sequence: 1,
                      },
                    ];
                  }

                  // Update roundState
                  onChangeRoundState({
                    ...roundState,
                    missionRules: _missionRules,
                    rules: _missionRules, // Keep rules in sync with missionRules
                  });

                  // Update newGameState as well
                  const newGameStateCopy = { ...newGameState };
                  if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                    newGameStateCopy.bracket.roundValues[
                      tabNo - 1
                    ].missionRules = _missionRules;
                    newGameStateCopy.bracket.roundValues[tabNo - 1].rules =
                      _missionRules; // Keep rules in sync with missionRules
                  } else {
                    newGameStateCopy.bracket.roundValues.push({
                      ...roundState,
                      missionRules: _missionRules,
                      rules: _missionRules, // Keep rules in sync with missionRules
                    });
                  }
                  onChangeNewGameState(newGameStateCopy);
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: '12px', marginBottom: '16px' }}>
            <div
              onClick={addKpiRule}
              style={{
                color: '#FF6A00',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              + Add KPI
            </div>
          </div>
        </>
      )}
    </>
  );
};

const RoundRules: React.FC<Props> = ({
  onChangeRoundState,
  roundState,
  kpis,
  newGameState,
  onChangeNewGameState,
  tabNo,
  errorMessages,
}) => {
  const [showMultipleKpis, setShowMultipleKpis] = useState(
    roundState.useMultipleKPIs || false
  );
  const [newPointsPerAction, setNewPointsPerAction] = useState('1.0');

  // Needed for accessing the rule on a KPI when selecting a rule.
  const normalizeKPIs = (kpis: KPI[]): Record<string, KPI> => {
    return kpis.reduce((acc, kpi) => {
      acc[kpi.value] = kpi;
      return acc;
    }, {} as Record<string, KPI>);
  };

  const normalizedKPIs = normalizeKPIs(kpis);

  // Need to update newGameState rules every time tabNo 1's rules change to match mobile logic:
  useEffect(() => {
    if (tabNo === 1) {
      onChangeNewGameState({
        ...newGameState,
        missionRules: roundState.missionRules,
        rules: roundState.missionRules, // Keep rules in sync with missionRules
      });
    }
  }, [roundState.missionRules]);

  // const addNewKpi = () => {
  //   // Convert from single KPI to multiple KPIs
  //   setShowMultipleKpis(true);

  //   // Create mission rule array from current KPI if it exists
  //   let initialRules: MissionRule[] = [];
  //   if (roundState.KPIRuleID) {
  //     initialRules = [
  //       {
  //         id: roundState.KPIRuleID,
  //         name: normalizedKPIs[roundState.KPIRuleID]?.label || '',
  //         multiplier: 1.0,
  //         role: 'contestant',
  //         sequence: 1,
  //       },
  //     ];
  //   }

  //   // Update roundState
  //   onChangeRoundState({
  //     ...roundState,
  //     useMultipleKPIs: true,
  //     missionRules: initialRules,
  //     rules: initialRules,
  //   });

  //   // Update newGameState
  //   const newGameStateCopy = { ...newGameState };
  //   if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
  //     newGameStateCopy.bracket.roundValues[tabNo - 1].useMultipleKPIs = true;
  //     newGameStateCopy.bracket.roundValues[tabNo - 1].missionRules =
  //       initialRules;
  //     newGameStateCopy.bracket.roundValues[tabNo - 1].rules = initialRules;
  //   } else {
  //     newGameStateCopy.bracket.roundValues.push({
  //       ...roundState,
  //       useMultipleKPIs: true,
  //       missionRules: initialRules,
  //       rules: initialRules,
  //     });
  //   }
  //   onChangeNewGameState(newGameStateCopy);
  // };

  return (
    <RulesAndRewardsViewContainer>
      <SectionTitle style={{ marginBottom: 8 }}>Rules</SectionTitle>
      <CustomToggleSwitch
        label="Use Multiple KPIs"
        value={showMultipleKpis}
        onChange={(newValue: any) => {
          console.log(
            'Toggle switch changed to:',
            newValue,
            'for round',
            tabNo
          );
          console.log('Current round state:', {
            useMultipleKPIs: roundState.useMultipleKPIs,
            missionRules: JSON.stringify(roundState.missionRules || []),
            KPIRuleID: roundState.KPIRuleID,
          });

          // When switching to multiple KPIs mode
          if (newValue) {
            // If we have a single KPI rule already, convert it to the first mission rule
            const initialRules = [];
            if (roundState.KPIRuleID) {
              console.log(
                'Converting single KPI to first mission rule for round',
                tabNo,
                ':',
                roundState.KPIRuleID
              );

              // Create a new rule based on the currently selected KPI
              initialRules.push({
                id: roundState.KPIRuleID,
                name:
                  roundState.KPIRuleName ||
                  normalizedKPIs[roundState.KPIRuleID]?.label ||
                  '',
                multiplier: 1.0, // Default multiplier
                role: 'contestant', // Default role
                sequence: 1,
              });
            } else if (kpis && kpis.length > 0) {
              // If no KPI is selected, use the first available KPI
              console.log(
                'No KPI selected, using first available KPI for round',
                tabNo
              );
              initialRules.push({
                id: kpis[0].value,
                name: kpis[0].label,
                multiplier: 1.0,
                role: 'contestant',
                sequence: 1,
              });
            }

            console.log(
              'Initial rules for multiple KPI mode for round',
              tabNo,
              ':',
              JSON.stringify(initialRules)
            );

            // Create updated round state
            const updatedRoundState = {
              ...roundState,
              useMultipleKPIs: newValue,
              missionRules: initialRules,
              rules: initialRules,
            };

            // Set KPIRuleID from the first rule if available
            if (initialRules.length > 0) {
              updatedRoundState.KPIRuleID = initialRules[0].id;
              updatedRoundState.KPIRuleName = initialRules[0].name;

              // Find next available KPI for selection
              const usedKpiIds = initialRules.map((rule) => rule.id);
              const nextAvailableKpi = kpis.find(
                (kpi) => !usedKpiIds.includes(kpi.value)
              );

              if (nextAvailableKpi) {
                updatedRoundState.KPIRuleID = nextAvailableKpi.value;
                updatedRoundState.KPIRuleName = nextAvailableKpi.label;
              }
            }

            // Update roundState
            onChangeRoundState(updatedRoundState);

            // Update newGameState as well
            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[tabNo - 1] = {
                ...newGameStateCopy.bracket.roundValues[tabNo - 1],
                ...updatedRoundState,
              };
            } else {
              newGameStateCopy.bracket.roundValues.push(updatedRoundState);
            }

            // If this is the last round, also update the main game's KPIRuleID
            if (tabNo === newGameState.totalRounds && initialRules.length > 0) {
              // Always use the first rule for the main game state
              const firstRule = initialRules[0];
              newGameStateCopy.KPIRuleID = firstRule.id;
              newGameStateCopy.KPIRuleName = firstRule.name;
            }

            onChangeNewGameState(newGameStateCopy);

            // Show alert about multiple KPIs (only once per session)
            GamifyToast.success(
              "If your game has multiple KPI's, you will be required to define the point value of each KPI in the game."
            );
          }
          // When switching from multiple KPIs to single KPI mode
          else if (
            roundState.missionRules &&
            roundState.missionRules.length > 0
          ) {
            // Use the first mission rule for the single KPI mode
            const firstRule = roundState.missionRules[0];
            console.log(
              'Switching to single KPI mode using first rule for round',
              tabNo,
              ':',
              JSON.stringify(firstRule)
            );

            // Create updated round state
            const updatedRoundState = {
              ...roundState,
              useMultipleKPIs: newValue,
              KPIRuleID: firstRule.id,
              KPIRuleName: firstRule.name,
              // Keep the missionRules in case we switch back
            };

            // Update roundState
            onChangeRoundState(updatedRoundState);

            // Update newGameState as well
            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[tabNo - 1] = {
                ...newGameStateCopy.bracket.roundValues[tabNo - 1],
                ...updatedRoundState,
              };
            } else {
              newGameStateCopy.bracket.roundValues.push(updatedRoundState);
            }

            // If this is the last round, ensure the main KPIRuleID is set
            if (tabNo === newGameState.totalRounds) {
              newGameStateCopy.KPIRuleID = firstRule.id;
              newGameStateCopy.KPIRuleName = firstRule.name;
            }

            onChangeNewGameState(newGameStateCopy);
          }
          // If no KPIs, just toggle the mode
          else {
            console.log(
              'No KPIs available for round',
              tabNo,
              ', just toggling mode'
            );

            // Create updated round state
            const updatedRoundState = {
              ...roundState,
              useMultipleKPIs: newValue,
            };

            // Update roundState
            onChangeRoundState(updatedRoundState);

            // Update newGameState as well
            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[tabNo - 1] = {
                ...newGameStateCopy.bracket.roundValues[tabNo - 1],
                ...updatedRoundState,
              };
            } else {
              newGameStateCopy.bracket.roundValues.push(updatedRoundState);
            }
            onChangeNewGameState(newGameStateCopy);
          }

          // Update local state variable
          setShowMultipleKpis(newValue);
        }}
      />
      {showMultipleKpis ? (
        renderMultipleKpiInputs(
          newGameState,
          onChangeNewGameState,
          roundState,
          onChangeRoundState,
          kpis,
          normalizedKPIs,
          tabNo,
          newPointsPerAction,
          setNewPointsPerAction,
          errorMessages
        )
      ) : (
        <>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '0px' }}>
            <div style={{ flex: 1 }}>
              <CustomSelect
                label="KPI*"
                value={roundState.KPIRuleID}
                error={errorMessages ? Boolean(errorMessages.KPIRuleID) : false}
                errorMessage={
                  errorMessages ? errorMessages.KPIRuleID || '' : ''
                }
                onChange={(newValue: any) => {
                  // Update the round state first
                  onChangeRoundState({
                    ...roundState,
                    KPIRuleID: newValue,
                    KPIRuleName: normalizedKPIs[newValue]?.label || '',
                  });

                  // Update newGameState for the bracket
                  const newGameStateCopy = { ...newGameState };
                  if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                    newGameStateCopy.bracket.roundValues[tabNo - 1].KPIRuleID =
                      newValue;
                    newGameStateCopy.bracket.roundValues[
                      tabNo - 1
                    ].KPIRuleName = normalizedKPIs[newValue]?.label || '';
                  } else {
                    newGameStateCopy.bracket.roundValues.push({
                      ...roundState,
                      KPIRuleID: newValue,
                      KPIRuleName: normalizedKPIs[newValue]?.label || '',
                    });
                  }

                  // If this is the last round, also set the main game KPIRuleID
                  if (tabNo === newGameState.totalRounds) {
                    newGameStateCopy.KPIRuleID = newValue;
                    newGameStateCopy.KPIRuleName =
                      normalizedKPIs[newValue]?.label || '';

                    // Also log that we're doing this as a fix for validation
                  }

                  onChangeNewGameState(newGameStateCopy);
                }}
                items={kpis}
              />
            </div>
            <div style={{ flex: 1 }}>
              <NumberInput
                label="Points Per Action*"
                value={(() => {
                  if (roundState.missionRules && roundState.KPIRuleID) {
                    const rule = roundState.missionRules.find(
                      (rule) => rule.id === roundState.KPIRuleID
                    );
                    return rule?.multiplier?.toString() || '1.0';
                  }
                  return '1.0';
                })()}
                onChangeText={(newValue: string) => {
                  // Create or update the mission rule with the new multiplier value
                  let _missionRules = roundState.missionRules || [];
                  const ruleIndex = _missionRules.findIndex(
                    (rule) => rule.id === roundState.KPIRuleID
                  );

                  if (ruleIndex >= 0) {
                    // Update existing rule
                    _missionRules[ruleIndex] = {
                      ..._missionRules[ruleIndex],
                      multiplier: parseFloat(newValue),
                    };
                  } else if (roundState.KPIRuleID) {
                    // Create new rule
                    _missionRules = [
                      {
                        id: roundState.KPIRuleID,
                        name: normalizedKPIs[roundState.KPIRuleID]?.label || '',
                        multiplier: parseFloat(newValue),
                        role: 'contestant',
                        sequence: 1,
                      },
                    ];
                  }

                  // Update roundState
                  onChangeRoundState({
                    ...roundState,
                    missionRules: _missionRules,
                    rules: _missionRules, // Keep rules in sync with missionRules
                  });

                  // Update newGameState as well
                  const newGameStateCopy = { ...newGameState };
                  if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                    newGameStateCopy.bracket.roundValues[
                      tabNo - 1
                    ].missionRules = _missionRules;
                    newGameStateCopy.bracket.roundValues[tabNo - 1].rules =
                      _missionRules; // Keep rules in sync with missionRules
                  } else {
                    newGameStateCopy.bracket.roundValues.push({
                      ...roundState,
                      missionRules: _missionRules,
                      rules: _missionRules, // Keep rules in sync with missionRules
                    });
                  }
                  onChangeNewGameState(newGameStateCopy);
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '0px' }}>
            <CustomSelect
              label="User's Role"
              value={(() => {
                // Find the rule for the current KPI
                if (roundState.missionRules && roundState.KPIRuleID) {
                  const rule = roundState.missionRules.find(
                    (rule) => rule.id === roundState.KPIRuleID
                  );
                  return rule?.role || 'contestant';
                }
                return 'contestant';
              })()}
              onChange={(newValue: any) => {
                // Create or update the single mission rule
                let _missionRules = roundState.missionRules || [];

                // Check if we already have a rule for this KPI
                const ruleIndex = _missionRules.findIndex(
                  (rule) => rule.id === roundState.KPIRuleID
                );

                if (ruleIndex >= 0) {
                  // Update existing rule
                  _missionRules[ruleIndex] = {
                    ..._missionRules[ruleIndex],
                    role: newValue,
                  };
                } else if (roundState.KPIRuleID) {
                  // Create new rule for the selected KPI
                  _missionRules = [
                    {
                      id: roundState.KPIRuleID,
                      name: normalizedKPIs[roundState.KPIRuleID]?.label || '',
                      multiplier: 1.0,
                      role: newValue,
                      sequence: 1,
                    },
                  ];
                }

                // Update roundState
                onChangeRoundState({
                  ...roundState,
                  missionRules: _missionRules,
                  rules: _missionRules, // Keep rules in sync with missionRules
                });

                // Update newGameState
                const newGameStateCopy = { ...newGameState };
                if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
                  newGameStateCopy.bracket.roundValues[tabNo - 1].missionRules =
                    _missionRules;
                  newGameStateCopy.bracket.roundValues[tabNo - 1].rules =
                    _missionRules; // Keep rules in sync with missionRules
                } else {
                  newGameStateCopy.bracket.roundValues.push({
                    ...roundState,
                    missionRules: _missionRules,
                    rules: _missionRules, // Keep rules in sync with missionRules
                  });
                }
                onChangeNewGameState(newGameStateCopy);
              }}
              items={[
                {
                  label: 'Anyone',
                  value: 'contestant',
                },
                ...(hideSetterCloser
                  ? []
                  : [
                      {
                        label: 'Setter',
                        value: 'setter',
                      },
                      {
                        label: 'Closer',
                        value: 'closer',
                      },
                    ]),
              ]}
            />
          </div>
        </>
      )}
      <CustomSelect
        label="Tie Breaker KPI"
        value={roundState.tieKPIRuleID}
        onChange={(newValue: any) => {
          onChangeRoundState({ ...roundState, tieKPIRuleID: newValue });

          const newGameStateCopy = { ...newGameState };
          if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
            newGameStateCopy.bracket.roundValues[tabNo - 1].tieKPIRuleID =
              newValue;
          } else {
            newGameStateCopy.bracket.roundValues.push({
              ...roundState,
              tieKPIRuleID: newValue,
            });
          }
          onChangeNewGameState(newGameStateCopy);
        }}
        items={kpis}
      />
      <ItemSelectInput
        label="Goal"
        selectedItem={
          roundState.isActionCountCustom ? 'custom' : roundState.actionCount
        }
        onSelect={(newSelectedItem: { label: string; value: any } | null) => {
          if (!newSelectedItem) {
            // Deselect - clear the goal
            onChangeRoundState({
              ...roundState,
              isActionCountCustom: false,
              actionCount: '',
            });

            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[
                tabNo - 1
              ].isActionCountCustom = false;
              newGameStateCopy.bracket.roundValues[tabNo - 1].actionCount = '';
            } else {
              newGameStateCopy.bracket.roundValues.push({
                ...roundState,
                isActionCountCustom: false,
                actionCount: '',
              });
            }
            onChangeNewGameState(newGameStateCopy);
          } else if (newSelectedItem.value === 'custom') {
            onChangeRoundState({
              ...roundState,
              isActionCountCustom: true,
              actionCount: '',
            });

            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[
                tabNo - 1
              ].isActionCountCustom = true;
              newGameStateCopy.bracket.roundValues[tabNo - 1].actionCount = '';
            } else {
              newGameStateCopy.bracket.roundValues.push({
                ...roundState,
                isActionCountCustom: true,
                actionCount: '',
              });
            }
            onChangeNewGameState(newGameStateCopy);
          } else {
            onChangeRoundState({
              ...roundState,
              isActionCountCustom: false,
              actionCount: newSelectedItem.value,
            });

            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[
                tabNo - 1
              ].isActionCountCustom = false;
              newGameStateCopy.bracket.roundValues[tabNo - 1].actionCount =
                newSelectedItem.value;
            } else {
              newGameStateCopy.bracket.roundValues.push({
                ...roundState,
                isActionCountCustom: false,
                actionCount: newSelectedItem.value,
              });
            }
            onChangeNewGameState(newGameStateCopy);
          }
        }}
        items={[
          { label: '10', value: '10' },
          { label: '25', value: '25' },
          { label: '50', value: '50' },
          { label: 'Custom', value: 'custom' },
        ]}
        numColumns={4}
        allowDeselect={true}
      />
      {roundState.isActionCountCustom && (
        <CustomTextInput
          label="Custom Goal"
          placeholder="E.g. 15"
          value={roundState.actionCount}
          onChange={(newValue) => {
            onChangeRoundState({ ...roundState, actionCount: newValue });

            const newGameStateCopy = { ...newGameState };
            if (newGameStateCopy.bracket.roundValues[tabNo - 1]) {
              newGameStateCopy.bracket.roundValues[tabNo - 1].actionCount =
                newValue;
            } else {
              newGameStateCopy.bracket.roundValues.push({
                ...roundState,
                actionCount: newValue,
              });
            }
            onChangeNewGameState(newGameStateCopy);
          }}
        />
      )}
    </RulesAndRewardsViewContainer>
  );
};

export default RoundRules;
